/**
 * Lo que lee el usuario cuando la IA no contesta — UN solo texto, en UN solo lugar.
 *
 * Lo usan el worker (aviso en vivo de la generación de análisis en lote, de la cotización
 * y de la hoja de planos), la API (504 `IA_TIMEOUT` de las consultas cortas de planos) y
 * las dos pantallas (cotización y planos). Antes había dos frases distintas para lo mismo
 * («El servicio de inteligencia artificial no está respondiendo…» en la cotización y «La IA
 * tardó demasiado en responder…» en planos) y la hoja de planos mostraba el error técnico
 * crudo («The operation was aborted due to timeout»).
 *
 * Reglas (CLAUDE.md §11): nunca nombra al proveedor ni al modelo, ni la mecánica interna;
 * «inteligencia artificial» como concepto sí. Tuteo es-MX, sin voseo.
 */

/**
 * Categoría CLIENT-SAFE de por qué falló algo hecho con IA. El detalle técnico va aparte
 * (log / staff), nunca a la pantalla del cliente.
 *  - `ai_unavailable`: el servicio de IA no respondió (timeout / caído).
 *  - `other`: cualquier otra cosa (respuesta inválida, error interno…).
 */
export type MotivoFalloIa = 'ai_unavailable' | 'other';

/** El servicio de IA no está respondiendo (timeouts / caído). */
export const MENSAJE_IA_NO_RESPONDE =
  'El servicio de inteligencia artificial no está respondiendo en este momento. Espera unos minutos y vuelve a intentar.';

/** Falló por otra razón (el servicio sí respondió). */
export const MENSAJE_IA_NO_SE_PUDO_GENERAR =
  'No se pudo completar la generación de análisis con inteligencia artificial. Vuelve a intentar en unos minutos.';

/** El texto para el usuario según la categoría del fallo. Lo desconocido cae en `other`. */
export function mensajeDeFalloIa(motivo: string | null | undefined): string {
  return motivo === 'ai_unavailable' ? MENSAJE_IA_NO_RESPONDE : MENSAJE_IA_NO_SE_PUDO_GENERAR;
}

/** Normaliza lo que venga de la base a la categoría client-safe (null si no hay fallo). */
export function motivoFalloIa(raw: string | null | undefined): MotivoFalloIa | null {
  if (raw == null || raw === '') return null;
  return raw === 'ai_unavailable' ? 'ai_unavailable' : 'other';
}

/**
 * Clasifica un error CRUDO de IA (texto técnico guardado, p. ej. `meta.aiError` del resumen) en la
 * categoría client-safe: los timeouts y la IA no disponible son `ai_unavailable`; el resto `other`.
 * Para pantallas que solo tienen el texto técnico y no una categoría guardada.
 */
export function motivoDeErrorDeIa(crudo: string | null | undefined): MotivoFalloIa {
  return /timeout|timed out|abort|no disponible|unavailable|ECONN|fetch failed/i.test(crudo ?? '')
    ? 'ai_unavailable'
    : 'other';
}
